import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import ContentBlock from "@/components/wpl/content-block/src";
import Image, { Picture } from "@/components/wpl/image/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import {
    BGProps,
    Row,
    StyledBackground
} from "@/libraries/wpl/foundations/src";
import PatternLogos from "@/patterns/wpl/logos/src";

const sectionStyle = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".jsm-customers-section": {
        "overflow": "hidden",
        "padding": `${token("space.200")} 0`,
        "WebkitFontSmoothing": "antialiased",
        "MozOsxFontSmoothing": "grayscale",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".jsm-customers-header": {
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            h2: {
                fontSize: "2.25rem",
                fontWeight: 400,
                marginBlockEnd: token("space.300"),
                lineHeight: 1.33333333,
                [media.below.sm]: {
                    fontSize: "1.5rem",
                    marginBlockEnd: token("space.200")
                }
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            a: {
                fontWeight: 300,
                marginBlockStart: token("space.0")
            }
        }
    }
});

const customerQuoteStyle = css({
    marginBlockStart: token("space.200")
});

const image = css({
    width: "115px"
});

const blurbStyle = css({
    fontSize: "1.5rem",
    fontWeight: 300,
    lineHeight: 1.3,
    margin: `${token("space.200")} 0`,
    [media.below.sm]: {
        fontSize: "1.25rem",
        marginBlockEnd: token("space.200"),
        letterSpacing: "0.3px"
    }
});

const nameStyle = css({
    fontSize: "0.75rem",
    fontWeight: 400,
    [media.below.sm]: {
        marginBlockEnd: token("space.200"),
        letterSpacing: "1px",
        lineHeight: 2
    }
});

const benefitStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "1.5rem",
    margin: `${token("space.200")} 0`,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    h3: {
        fontSize: "2.625rem",
        fontWeight: 500,
        margin: token("space.0")
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    p: {
        fontSize: "1rem",
        fontWeight: 400,
        margin: token("space.0")
    }
});

const inlineLogosProp = [
    {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/8hil7171bgf726i008u426ly20lmckpd.svg",
        alt: "Square logo",
        width: "144px"
    },
    {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/1728do7q372772c56nn1d6176lgv7g41.svg",
        alt: "Sotheby's logo",
        width: "146px"
    },
    {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/ol564g48h6368my8s6v44fcc1w7jtds1.svg",
        alt: "Domino's logo",
        width: "162px"
    },
    {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/58qk6136v662bx6i084qm16f383471s2.svg",
        alt: "Puppet logo",
        width: "99px"
    },
    {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/8h645v2kexjar2qvcn757ao4cws2jj8u.svg",
        alt: "Twitter logo",
        width: "43px"
    },
    {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/5vhj2x4xck7s3f4gjhtin3ges02a2mn0.svg",
        alt: "The Telegraph logo",
        width: "219px"
    }
];

const patternLogosJSMCustomersSectionStyle = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".row-layout": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".logos-content": {
            "display": "flex",
            "flexWrap": "wrap",
            "gap": token("space.0"),
            "alignItems": "center",
            "justifyContent": "center",
            "margin": "auto",
            [media.below.sm]: {
                maxWidth: "200px"
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ".logoblock": {
                "margin": token("space.200"),
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                "+ img": {
                    marginBlockEnd: token("space.200"),
                    verticalAlign: "bottom",
                    pointEvents: "none",
                    maxWidth: "100%"
                }
            }
        }
    }
});

type BenefitProps = {
    title: string;
    description: string;
};

const Benefit = ({ title, description }: BenefitProps) => (
    <div css={benefitStyle}>
        <h3>{title}</h3>
        <p>{description}</p>
    </div>
);

type CustomerQuoteProps = {
    quote: string;
    name: string;
    imageLink: string;
    imageAltText: string;
    benefits: Array<BenefitProps>;
};

const CustomerQuote = ({
    quote,
    name,
    imageLink,
    imageAltText,
    benefits
}: CustomerQuoteProps) => (
    <div css={customerQuoteStyle}>
        <Image src={imageLink} alt={imageAltText} css={image} />
        <p css={blurbStyle}>{quote}</p>
        <p css={nameStyle}>{name}</p>
        {benefits.map((benefit, index) => (
            <Benefit key={`${benefit.title}-${index}`} {...benefit} />
        ))}
    </div>
);

const CustomersSection = () => {
    const intl = useIntl();

    const benefits: Array<BenefitProps> = [
        {
            title: "67%",
            description: intl.formatMessage({
                id: "jira-service-management.customers.benefits.one",
                defaultMessage:
                    "reduction on license costs from switching to Jira Service Management"
            })
        },
        {
            title: "200",
            description: intl.formatMessage({
                id: "jira-service-management.customers.benefits.two",
                defaultMessage: "hours saved a month with report automation"
            })
        },
        {
            title: "100%",
            description: intl.formatMessage({
                id: "jira-service-management.customers.benefits.three",
                defaultMessage: "SLA compliance"
            })
        }
    ];

    const backgroundProperties: BGProps = {
        textColor: "#ffffff",
        gradientDirection: "180",
        bgColor: "#6554c0",
        bgColor2: "#091e42"
    };
    return (
        <section css={sectionStyle}>
            <StyledBackground {...backgroundProperties}>
                <div className="jsm-customers-section">
                    <Row alignItems="top">
                        <ContentBlock
                            className="jsm-customers-header"
                            headerText={intl.formatMessage({
                                id: "jira-service-management.customers.title",
                                defaultMessage:
                                    "Teams across the world rely on Jira Service Management"
                            })}
                            headerType="h2"
                            isCentered={true}
                            ctaProps={{
                                primaryLinkProps: {
                                    text: intl.formatMessage({
                                        id: "jira-service-management.customers.primaryLink",
                                        defaultMessage:
                                            "Explore customer stories"
                                    }),
                                    href: useLocalizedHref(
                                        "/software/jira/service-management/customers"
                                    ),
                                    type: "primary",
                                    bgColor: "bgBlackBlue",
                                    dataEventProps: {
                                        "data-event-component": "link",
                                        "data-event-container":
                                            "customersSection",
                                        "data-event": "clicked",
                                        "data-product-key":
                                            "jiraServiceManagement"
                                    }
                                }
                            }}
                        />
                    </Row>
                    <Row layout="7-5" alignItems="center">
                        <Picture
                            image={{
                                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/10l08o2vaka472w5s7t2xn36574bt4w0.svg",
                                alt: intl.formatMessage({
                                    id: "jira-service-management.customers.imageAltText",
                                    defaultMessage:
                                        "Coworkers collaborating through a series of tasks: documents, charts, video calls, and a service desk"
                                })
                            }}
                        />
                        <CustomerQuote
                            imageLink="https://dam-cdn.atl.orangelogic.com/AssetLink/7n5j0hqxeq3dci261cgq33ubb03k2886.svg"
                            imageAltText="Engie"
                            quote={intl.formatMessage({
                                id: "jira-service-management.customers.quote",
                                defaultMessage:
                                    "“We needed to move towards a more customer-centric approach on ITSM. Jira Service Management helped us meet our target while saving time and money.”"
                            })}
                            name={intl.formatMessage({
                                id: "jira-service-management.customers.name",
                                defaultMessage:
                                    "JOSE LUIS LIZÁRRAGA CASTRO, IT SUPPORT ENGINEER, ENGIE MEXICO"
                            })}
                            benefits={benefits}
                        />
                    </Row>
                    <PatternLogos
                        css={patternLogosJSMCustomersSectionStyle}
                        inlineLogos={inlineLogosProp}
                    />
                </div>
            </StyledBackground>
        </section>
    );
};

export default CustomersSection;
